/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.16.0
 */

import { useMemo } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { InfoBlock } from '@components/common';
import { EColorStatus } from '@modules/app/appConstants';
import { makeSelectTranslationWizardImportPersonalData } from '@modules/internationalization/intSelectors';
import {
    IWizardImportPersonalDataTranslation,
} from '@modules/internationalization/types/wizards/importPersonalData';
import { IRootReducer } from '@reducers/rootReducer';
import { stringFormatParams } from '@utils/stringFormat';

import { EWizardImportPersonalDataSteps } from '../wizardImportPersonalDataConstant';
import { IWizardImportPersonalDataReducer } from '../wizardImportPersonalDataReducer';
import {
    makeSelectWizardImportPersonalDataErrorRowsAmount,
    makeSelectWizardImportPersonalDataFileRowsAmount,
    makeSelectWizardImportPersonalDataRemovedRowsAmount,
} from '../wizardImportPersonalDataSelectors';
import { WizardImportPersonalDataStyled } from './styled/WizardImportPersonalDataStyled';
import WizardImportPersonalDataTable from './WizardImportPersonalDataTable/WizardImportPersonalDataTable';

const WizardImportPersonalDataConfirmation = (props: IConnectedProps) => {
    const {
        errorRowsAmount,
        fileRowsAmount,
        removedRowsAmount,
        translation,
    } = props;

    const stepTranslation = translation[EWizardImportPersonalDataSteps.ConfirmImportStep];

    const getCorrectRowsAmount = useMemo(() => {
        return fileRowsAmount > (errorRowsAmount + removedRowsAmount)
            ? fileRowsAmount - errorRowsAmount - removedRowsAmount
            : 0;
    }, [errorRowsAmount, fileRowsAmount, removedRowsAmount]);

    return <WizardImportPersonalDataStyled>
        <InfoBlock className="wizard-import-personal-data__warning" colorStatus={EColorStatus.WARNING}>
            <div dangerouslySetInnerHTML={{
                __html: stepTranslation.warning.picInformation,
            }}/>
        </InfoBlock>
        <InfoBlock className="wizard-import-personal-data__warning" colorStatus={EColorStatus.WARNING}>
            {stepTranslation.warning.personalDataConfirmation}
        </InfoBlock>
        <div className="wizard-import-personal-data__export-errors">
            <div>
                {stringFormatParams(stepTranslation.counterLabel, {
                    correctRowsAmount: getCorrectRowsAmount,
                    fileRowsAmount,
                })}
            </div>
        </div>
        <div className="wizard-import-personal-data__table">
            <WizardImportPersonalDataTable
                translation={translation}
            />
        </div>
    </WizardImportPersonalDataStyled>;
};

export interface IConnectedProps extends Pick<IWizardImportPersonalDataReducer,
    'errorRowsAmount' | 'fileRowsAmount' | 'removedRowsAmount'> {
    translation: IWizardImportPersonalDataTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    errorRowsAmount: makeSelectWizardImportPersonalDataErrorRowsAmount(),
    fileRowsAmount: makeSelectWizardImportPersonalDataFileRowsAmount(),    
    removedRowsAmount: makeSelectWizardImportPersonalDataRemovedRowsAmount(),
    translation: makeSelectTranslationWizardImportPersonalData(),
});

export default connect(mapStateToProps) (WizardImportPersonalDataConfirmation);
